import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';

import { Typography, Container, Divider, Button, SepaIcon } from '@ag1/ui';
import { useSessionValue } from '@portal/appLayer/auth';
import { routes } from '@portal/constants/routes';
import { SUBSCRIPTION_API_URL } from '@portal/config/base';
import { SepaPage } from '@portal/schema/studio/commonContentSchema';
import { showNotification } from '@portal/shared/ui/Notification';
import SupportContact from '@portal/shared/ui/Support';
import { useDate, useInternalApiRequest } from '@portal/shared/hooks';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { pluralize } from '@portal/shared/utils/pluralize';
import { capitalize } from '@portal/shared/utils/capitalize';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useStoreCurrency } from '@portal/state/hooks/useStoreCurrency';
import { getQueryKeyForGetSubscriptionsV2 } from '@portal/state/internal/getSubscriptions';
import { useQueryGetCommonContentData } from '@portal/state/studio/commonContentData/getCommonContentData';
import Layout from 'shared/layouts/BaseLayout';

import { EmptyState } from '../HomeView/EmptyState';
import styles from './SepaView.module.scss';
import { SepaData } from './SepaView.types';
import { CancelSepaModal } from './CancelSepaModal';

const SepaView = ({ sepaData }: { sepaData: SepaData }) => {
  const router = useRouter();
  const locale = useLocale();
  const store = useStoreValue();
  const queryClient = useQueryClient();
  const request = useInternalApiRequest();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const { symbolCurrency } = useStoreCurrency();
  const { data: commonContent } = useQueryGetCommonContentData({ locale }) || {};
  const session = useSessionValue();
  const customer = session?.customer;

  const {
    welcomeMessage,
    subscriptionDetailsTitle,
    subscriptionDetailsNextOrderDate,
    subscriptionDetailsFrequency,
    contactInformationTitle,
    shippingAddressTitle,
    shippingMethodTitle,
    paymentMethodTitle,
    updateSubscriptionButton,
    cancelSubscriptionButton,
    sepaSupportMessage,
    sepaSupportCard,
    sepaCancellationModal
  } = (commonContent?.sepaPage as SepaPage) || {};
  const { title, businessHours, description, linkFAQ, phone, phoneHref } = sepaSupportCard || {};

  const handleSubmit = useCallback(
    (resubscribe: boolean) => {
      if (!customer?.id) {
        showNotification('mutationError', {
          title: 'Missing customer ID.'
        });
        return;
      }

      setIsCancelLoading(true);

      request({
        method: 'PUT',
        url: `${SUBSCRIPTION_API_URL[store]}/user/sepa/${customer.id}/cancel`
      })
        .then(async () => {
          if (resubscribe) {
            await router.push(routes.buildBundleShopify);
          }
          queryClient.invalidateQueries({
            queryKey: getQueryKeyForGetSubscriptionsV2(store)
          });
        })
        .catch(() => {
          showNotification('mutationError', {
            title: 'Something went wrong. Please refresh the page or try again later.'
          });
        })
        .finally(() => {
          setIsModalOpen(false);
          setIsCancelLoading(false);
        });
    },
    [router, customer, store, request, queryClient]
  );

  const { formattedDateString: nextOrderDate } = useDate({
    date: sepaData.next_order_date,
    dateFormat: 'MMM d, yyyy',
    fallbackString: 'N/A'
  });

  const sections = useMemo(() => {
    if (!sepaData || !customer) return [];

    const {
      line_items: lineItems,
      frequency_count: frequencyCount,
      frequency_interval: frequencyInterval,
      total_order_cost: totalOrderCost,
      shipping_address: shippingAddress,
      shipping_method: shippingMethod,
      sepa_email: sepaEmail
    } = sepaData;

    const lineItemsFormatted = lineItems
      .map((item) => `${item.product.name} x ${item.quantity}`)
      .join('\n');

    const frequencyText = `Every ${pluralize(
      frequencyCount,
      capitalize(frequencyInterval.toLocaleLowerCase())
    )}`;
    const detailsFormatted = `${subscriptionDetailsNextOrderDate} ${nextOrderDate}\n${subscriptionDetailsFrequency} ${frequencyText}\n\n${lineItemsFormatted}`;

    const {
      first_name: firstName,
      last_name: lastName,
      address_1: address1,
      city,
      province_code: provinceCode,
      zip
    } = shippingAddress;
    const shippingAddressFormatted = `${firstName} ${lastName}\n${address1}, ${city}, ${provinceCode}, ${zip}`;
    const { presentment_title: shipTitle } = shippingMethod;
    const paymentMethodFormatted = `${sepaEmail} - ${symbolCurrency}${totalOrderCost}`;

    return [
      {
        title: subscriptionDetailsTitle,
        content: detailsFormatted,
        bottomSpacing: '24px'
      },
      {
        title: contactInformationTitle,
        content: customer.email,
        bottomSpacing: '21px'
      },
      {
        title: shippingAddressTitle,
        content: shippingAddressFormatted,
        bottomSpacing: '21px'
      },
      {
        title: shippingMethodTitle,
        content: shipTitle,
        bottomSpacing: '14px'
      },
      {
        title: paymentMethodTitle,
        content: (
          <>
            <div style={{ display: 'flex', paddingRight: '8px' }}>
              <SepaIcon />
            </div>
            {paymentMethodFormatted}
          </>
        ),
        bottomSpacing: '0px'
      }
    ];
  }, [
    sepaData,
    customer,
    nextOrderDate,
    symbolCurrency,
    contactInformationTitle,
    paymentMethodTitle,
    shippingAddressTitle,
    shippingMethodTitle,
    subscriptionDetailsFrequency,
    subscriptionDetailsNextOrderDate,
    subscriptionDetailsTitle
  ]);

  if (!commonContent) return null;

  if (!sepaData) return <EmptyState />;

  return (
    <Layout>
      <CancelSepaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={(resubscribe) => handleSubmit(resubscribe)}
        isCancelLoading={isCancelLoading}
        modalData={sepaCancellationModal}
      />
      <Container className={styles.sepaContainer}>
        <Typography variant="headline" size="2" noPadding className={styles.sepaHeader}>
          {welcomeMessage} {customer?.firstName}
        </Typography>
        <Divider top={40} bottom={{ xxs: 24, m: 40 }} />
        <div className={styles.sepaContent}>
          <div className={styles.sepaDetails}>
            {sections.map((section) => (
              <div
                key={section.title}
                className={styles.section}
                style={{ paddingBottom: section.bottomSpacing }}>
                <Typography variant="portalText" size="1" weight="700" noPadding>
                  {section.title}
                </Typography>
                <Typography variant="portalText" size="1" noPadding className={styles.lineContent}>
                  {section.content}
                </Typography>
              </div>
            ))}
          </div>
          <div className={styles.sepaActionsContainer}>
            <div className={styles.sepaActions}>
              <Button
                variant="outlineGray"
                size={{ xxs: 'small', m: 'medium', xxl: 'large' }}
                disabled>
                {updateSubscriptionButton}
              </Button>
              <Button
                variant="primary"
                size={{ xxs: 'small', m: 'medium', xxl: 'large' }}
                onClick={() => setIsModalOpen(true)}>
                {cancelSubscriptionButton}
              </Button>
            </div>
            <Typography variant="portalText" size="1" noPadding>
              {sepaSupportMessage}
            </Typography>
            <SupportContact
              title={title}
              phone={phone}
              businessHours={businessHours}
              phoneHref={phoneHref}
              description={description}
              linkFAQ={linkFAQ}
            />
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default SepaView;
